<template>
  <div class="dash">
    <!-- Header -->
    <div class="dash-header">
      <div>
        <h1 class="dash-title">Panel admin</h1>
        <p class="dash-subtitle">Bienvenido, {{ userName }}.</p>
      </div>
      <div v-if="menuItems.length > 0" class="dash-search-box">
        <i class="bi bi-search dash-search-icon"></i>
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          class="dash-search-input"
          placeholder="Buscar opciones..."
          @keydown.enter="goToFirstResult"
          @keydown.down.prevent="highlightNext"
          @keydown.up.prevent="highlightPrev"
          @keydown.escape="searchQuery = ''"
        />
        <kbd v-if="!searchQuery" class="dash-search-kbd">/</kbd>
        <button v-else class="dash-search-clear" @click="searchQuery = ''; $refs.searchInput.focus()">
          <i class="bi bi-x"></i>
        </button>
        <!-- Resultados -->
        <div v-if="searchResults.length > 0" class="dash-search-results">
          <router-link
            v-for="(item, idx) in searchResults"
            :key="item.Menu_id"
            :to="getRoutePath(item.Enlace)"
            :class="['dash-search-item', { 'is-active': idx === highlightIndex }]"
            @click="searchQuery = ''"
          >
            <i :class="'bi bi-' + (item.Imagen || 'grid')"></i>
            <span>{{ item.Titulo }}</span>
            <span class="dash-search-item-desc">{{ item.Descripcion || '' }}</span>
          </router-link>
        </div>
        <div v-else-if="searchQuery.length >= 2" class="dash-search-results">
          <div class="dash-search-empty">Sin resultados</div>
        </div>
      </div>
    </div>

    <!-- Menu groups -->
    <template v-if="menuGroups.length > 0 || standaloneItems.length > 0">
      <div v-for="group in menuGroups" :key="group.parentId" class="dash-section">
        <h2 class="dash-section-title">
          {{ group.parentTitle }}
        </h2>
        <div class="dash-grid">
          <router-link
            v-for="item in group.children"
            :key="item.Menu_id || item.MenuId"
            :to="getRoutePath(item.Enlace)"
            class="dash-card"
          >
            <div class="dash-card-body">
              <span class="dash-card-title">{{ item.Titulo }}</span>
              <span class="dash-card-desc">{{ item.Descripcion || '' }}</span>
            </div>
            <div class="dash-card-icon">
              <i :class="'bi bi-' + (item.Imagen || 'grid')"></i>
            </div>
          </router-link>
        </div>
      </div>

      <div v-if="standaloneItems.length > 0" class="dash-section">
        <div class="dash-grid">
          <router-link
            v-for="item in standaloneItems"
            :key="item.Menu_id || item.MenuId"
            :to="getRoutePath(item.Enlace)"
            class="dash-card"
          >
            <div class="dash-card-body">
              <span class="dash-card-title">{{ item.Titulo }}</span>
              <span class="dash-card-desc">{{ item.Descripcion || '' }}</span>
            </div>
            <div class="dash-card-icon">
              <i :class="'bi bi-' + (item.Imagen || 'grid')"></i>
            </div>
          </router-link>
        </div>
      </div>

    </template>
    <div v-else class="dash-empty">
      <i class="bi bi-shield-lock"></i>
      <p>No hay opciones disponibles para tu usuario en este panel.</p>
    </div>
  </div>
</template>

<script>
import authService from '@/services/auth';

function menuOrder(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 999999;
}

export default {
  name: 'AdminDashboardView',
  data() {
    return {
      menuItems: [],
      menuGroups: [],
      standaloneItems: [],
      searchQuery: '',
      highlightIndex: 0
    };
  },
  computed: {
    userName() {
      const u = authService.getUser()?.Usuario;
      const usuarioMo = u?._UsuarioMo ?? u?._usuarioMo;
      return usuarioMo?.Nombre ?? usuarioMo?.nombre ?? u?.UsuarioId ?? u?.usuarioId ?? 'Usuario';
    },
    searchResults() {
      if (!this.searchQuery || this.searchQuery.length < 2) return [];
      const q = this.searchQuery.toLowerCase();
      return this.menuItems
        .filter(item =>
          item.Enlace && item.Enlace !== '#' && item.Enlace.trim() !== '' &&
          (item.Titulo?.toLowerCase().includes(q) || item.Descripcion?.toLowerCase().includes(q))
        )
        .sort((a, b) => {
          const diff = menuOrder(a.Orden) - menuOrder(b.Orden);
          return diff !== 0 ? diff : (Number(a.Menu_id) || 0) - (Number(b.Menu_id) || 0);
        })
        .slice(0, 8);
    }
  },
  watch: {
    searchQuery() {
      this.highlightIndex = 0;
    }
  },
  created() {
    this.loadMenuItems();
  },
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.searchInput) this.$refs.searchInput.focus();
    });
    document.addEventListener('keydown', this._globalSlash);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this._globalSlash);
  },
  methods: {
    _globalSlash(e) {
      if (e.key === '/' && document.activeElement !== this.$refs.searchInput) {
        e.preventDefault();
        this.$refs.searchInput?.focus();
      }
    },
    goToFirstResult() {
      const results = this.searchResults;
      if (results.length > 0) {
        const item = results[this.highlightIndex] || results[0];
        const path = this.getRoutePath(item.Enlace);
        this.searchQuery = '';
        this.$router.push(path);
      }
    },
    highlightNext() {
      if (this.highlightIndex < this.searchResults.length - 1) this.highlightIndex++;
    },
    highlightPrev() {
      if (this.highlightIndex > 0) this.highlightIndex--;
    },
    getRoutePath(enlace) {
      if (!enlace) return '/';
      const p = (enlace.startsWith('/') ? enlace : '/' + enlace).toLowerCase();
      return p === '//' ? '/' : p;
    },
    norm(item) {
      if (!item || typeof item !== 'object') return null;
      const ok = v => v === true || v === 1 || v === '1' || v === 'true';
      return {
        Menu_id: item.Menu_id ?? item.MenuId ?? item.menuId,
        Menu_id_padre: item.Menu_id_padre ?? item.MenuIdPadre ?? item.menuIdPadre ?? 0,
        Titulo: item.Titulo ?? item.titulo ?? '',
        Descripcion: item.Descripcion ?? item.descripcion ?? '',
        Imagen: item.Imagen ?? item.imagen ?? '',
        Enlace: item.Enlace ?? item.enlace ?? '',
        Orden: item.Orden ?? item.orden ?? 999,
        Grupo: Number(item.Grupo ?? item.grupo ?? 0),
        Es_activo: ok(item.Es_activo ?? item.EsActivo ?? item.es_activo ?? item.esActivo)
      };
    },
    loadMenuItems() {
      const userMenu = authService.getUserMenu();
      if (userMenu && Array.isArray(userMenu)) {
        const normalized = userMenu.map(this.norm).filter(Boolean);
        this.menuItems = normalized
          .filter(item => item.Es_activo && item.Grupo === 2)
          .sort((a, b) => {
            const diff = menuOrder(a.Orden) - menuOrder(b.Orden);
            return diff !== 0 ? diff : (Number(a.Menu_id) || 0) - (Number(b.Menu_id) || 0);
          });

        const parentMenuIds = new Set();
        const parentMenuItems = {};

        this.menuItems.forEach(item => {
          const menuIdPadre = item.Menu_id_padre;
          if (menuIdPadre && menuIdPadre > 0) {
            parentMenuIds.add(menuIdPadre);
          }
        });

        this.menuGroups = [];
        this.standaloneItems = [];

        const menuItemsByParent = {};

        parentMenuIds.forEach(parentId => {
          menuItemsByParent[parentId] = [];
        });

        this.menuItems.forEach(item => {
          const menuId = item.Menu_id;
          const menuIdPadre = item.Menu_id_padre;

          if (menuIdPadre && menuIdPadre > 0 && parentMenuIds.has(menuIdPadre)) {
            if (!menuItemsByParent[menuIdPadre]) {
              menuItemsByParent[menuIdPadre] = [];
            }
            menuItemsByParent[menuIdPadre].push(item);

            if (!parentMenuItems[menuIdPadre]) {
              const parentItem = this.menuItems.find(parent => parent.Menu_id === menuIdPadre);
              if (parentItem) {
                parentMenuItems[menuIdPadre] = parentItem;
              }
            }
          }
          else if (menuId && parentMenuIds.has(menuId)) {
            parentMenuItems[menuId] = item;
          }
          else if (!menuIdPadre || menuIdPadre === 0) {
            const enlace = item.Enlace || '';
            if (enlace && enlace !== '#' && enlace.trim() !== '') {
              this.standaloneItems.push(item);
            }
          }
        });

        Object.keys(menuItemsByParent).forEach(parentId => {
          const children = menuItemsByParent[parentId];
          const parentItem = parentMenuItems[parentId];

          if (children.length > 0 && parentItem) {
            const sortedChildren = children.sort((a, b) => {
              const diff = menuOrder(a.Orden) - menuOrder(b.Orden);
              return diff !== 0 ? diff : (Number(a.Menu_id) || 0) - (Number(b.Menu_id) || 0);
            });

            this.menuGroups.push({
              parentId: parentId,
              parentTitle: parentItem.Titulo,
              parentIcon: parentItem.Imagen,
              parentOrden: parentItem.Orden,
              children: sortedChildren
            });
          }
        });

        this.menuGroups.sort((a, b) => {
          const diff = menuOrder(a.parentOrden) - menuOrder(b.parentOrden);
          return diff !== 0 ? diff : Number(a.parentId) - Number(b.parentId);
        });

        this.standaloneItems.sort((a, b) => {
          const diff = menuOrder(a.Orden) - menuOrder(b.Orden);
          return diff !== 0 ? diff : (Number(a.Menu_id) || 0) - (Number(b.Menu_id) || 0);
        });
      } else {
        this.menuItems = [];
        this.menuGroups = [];
        this.standaloneItems = [];
      }
    }
  }
}
</script>

<style scoped>
.dash {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem 1.25rem;
  font-family: inherit;
}

/* ========== Header ========== */
.dash-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.dash-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.dash-subtitle {
  font-size: 0.9rem;
  color: #718096;
  margin: 0.2rem 0 0;
}

/* ========== Search ========== */
.dash-search-box {
  position: relative;
  flex: 1;
  max-width: 420px;
  min-width: 200px;
}

.dash-search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 0.85rem;
  pointer-events: none;
}

.dash-search-input {
  width: 100%;
  padding: 0.55rem 2.2rem 0.55rem 2.2rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.85rem;
  font-family: inherit;
  color: #2d3748;
  background: #f8fafc;
  outline: none;
  transition: all 0.2s;
}

.dash-search-input:focus {
  border-color: #1272c4;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(18, 114, 196, 0.1);
}

.dash-search-input::placeholder {
  color: #a0aec0;
}

.dash-search-kbd {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: #edf2f7;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 0px 6px;
  font-size: 0.7rem;
  color: #a0aec0;
  font-family: monospace;
  line-height: 1.6;
}

.dash-search-clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  font-size: 1rem;
  padding: 0 4px;
  line-height: 1;
}

.dash-search-clear:hover {
  color: #4a5568;
}

.dash-search-results {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  z-index: 50;
  max-height: 320px;
  overflow-y: auto;
}

.dash-search-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 0.85rem;
  text-decoration: none;
  color: #2d3748;
  font-size: 0.85rem;
  transition: background 0.15s;
  cursor: pointer;
}

.dash-search-item:hover,
.dash-search-item.is-active {
  background: #eff6ff;
}

.dash-search-item i {
  color: #1272c4;
  font-size: 0.9rem;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.dash-search-item-desc {
  margin-left: auto;
  font-size: 0.72rem;
  color: #a0aec0;
}

.dash-search-empty {
  padding: 1rem;
  text-align: center;
  color: #a0aec0;
  font-size: 0.8rem;
}

/* ========== Section ========== */
.dash-section {
  margin-bottom: 2rem;
}

.dash-section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.6rem;
  font-weight: 400;
  color: #1a202c;
  margin: 0 0 1rem;
  padding: 0;
  border: none;
  text-transform: none;
  letter-spacing: normal;
}

.dash-section-title i {
  display: none;
}

/* ========== Grid ========== */
.dash-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 1.25rem;
}

/* ========== Card ========== */
.dash-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem 1.8rem;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  text-decoration: none;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  transition: all 0.2s ease;
  cursor: pointer;
}

.dash-card:hover {
  border-color: #9ca3af;
  box-shadow: 0 4px 6px rgba(0,0,0,0.08);
  transform: translateY(-2px);
  background: #fdfdfd;
}

.dash-card-icon {
  font-size: 2.5rem;
  color: #0d6efd;
  flex-shrink: 0;
}

.dash-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.dash-card-title {
  font-size: 1.1rem;
  color: #0d6efd;
  text-decoration: underline;
  margin-bottom: 0.2rem;
  font-weight: 400;
  line-height: 1.2;
}

.dash-card-desc {
  font-size: 0.95rem;
  color: #0d6efd;
  text-decoration: underline;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ========== Empty ========== */
.dash-empty {
  text-align: center;
  padding: 3rem 1rem;
  color: #a0aec0;
}

.dash-empty i {
  font-size: 2.5rem;
  color: #e2e8f0;
}

.dash-empty p {
  margin: 0.75rem 0 0;
  font-size: 0.9rem;
}

/* ========== Responsive ========== */
@media (max-width: 640px) {
  .dash-grid {
    grid-template-columns: 1fr;
  }

  .dash-header {
    flex-direction: column;
  }
}
</style>
